/* Northman "Build your kit": require-choice layer over Warehouse's <variant-picker>.
 * Markup: snippets/product-variant-selector.liquid (card) + snippets/hk-option-select.liquid (steps).
 * - Native <select>s carry no name/data-option-position, so the theme ignores them until every option is chosen.
 * - Once complete, the choices are copied into hidden optionN inputs (given data-option-position) and a change
 *   event hands over to Warehouse, which re-renders price, compare-at, SKU, stock line and buy buttons.
 * - While incomplete: Add to cart locked (aria-disabled + data-hk-locked, not `disabled`, so an early add can be
 *   caught), hint lists what is missing, early add pulses the missing steps red; SKU/stock/Buy-now hidden by CSS.
 * - UI state: step check badges, surcharge pill per step, "k of N" progress chip, "Your kit" summary + price.
 * Generic: any product whose variant_selector block has require_choice on (multi-variant products only).
 */
(function () {
  if (window.customElements.get('hk-option-picker')) return;

  var state = new Map(); // form id -> { position: optionValueId }

  function listSentence(names) {
    if (names.length <= 1) return names.join('');
    return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
  }

  function chosenOption(sel) {
    return sel.value ? sel.options[sel.selectedIndex] : null;
  }

  class HkOptionPicker extends HTMLElement {
    connectedCallback() {
      this.formId = this.getAttribute('form-id');
      this.scope = this.closest('variant-picker') || this.parentElement;
      this.selects = Array.from(this.scope.querySelectorAll('select[data-hk-option-position]'));
      try {
        this.variants = JSON.parse((this.scope.querySelector('script[data-hk-variants]') || {}).textContent || '[]');
      } catch (e) { this.variants = []; }
      if (!state.has(this.formId)) state.set(this.formId, {});
      var memo = state.get(this.formId);

      this.selects.forEach(function (sel) {
        var pos = sel.getAttribute('data-hk-option-position');
        var remembered = memo[pos];
        if (remembered && Array.from(sel.options).some(function (o) { return o.value === remembered; })) {
          sel.value = remembered; // survive the theme re-render of this block
        } else if (sel.value) {
          memo[pos] = sel.value; // server pre-selected (?variant=) or single-value option
        }
        sel.addEventListener('change', this.onChange.bind(this, sel));
      }, this);

      // Body Type step (folded option "<Body> · <range>", snippets/hk-body-step.liquid): keep every folded value, show only the
      // chosen body's values (range text only); derive the body from a pre-selected folded value (?variant=).
      this.bodySel = this.selects.find(function (s) { return s.getAttribute('data-hk-option-position') === 'body'; }) || null;
      this.foldSel = this.bodySel ? this.scope.querySelector('select[data-hk-fold]') : null;
      if (this.foldSel) {
        this.foldAll = Array.from(this.foldSel.options).filter(function (o) { return o.value; }).map(function (o) {
          var n = o.getAttribute('data-hk-name') || ''; var i = n.indexOf(' · ');
          return { value: o.value, name: n, body: i > -1 ? n.slice(0, i) : '', range: i > -1 ? n.slice(i + 3) : n,
                   sur: o.getAttribute('data-surcharge') || '0', pill: o.getAttribute('data-surcharge-label') || '', text: o.textContent, sold: o.hasAttribute('data-sold-out') };
        });
        if (!this.bodySel.value && this.foldSel.value) {
          var cur = this.foldAll.find(function (a) { return a.value === this.foldSel.value; }, this);
          if (cur) { this.bodySel.value = cur.body; memo.body = cur.body; }
        }
        this.filterFold(false);
      }

      this.form = document.forms[this.formId];
      this._onRerender = function () { window.requestAnimationFrame(this.sync.bind(this, false)); }.bind(this);
      if (this.form) this.form.addEventListener('product:rerender', this._onRerender);
      this.sync(false);
      // This block is replaced before the buy buttons during a Warehouse re-render: re-sync once it has finished.
      window.requestAnimationFrame(this.sync.bind(this, false));
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('product:rerender', this._onRerender);
    }

    filterFold(fromChange) {
      var fs = this.foldSel, body = this.bodySel.value, memo = state.get(this.formId), pos = fs.getAttribute('data-hk-option-position');
      var keep = fs.value && this.foldAll.some(function (a) { return a.value === fs.value && a.body === body; }) ? fs.value : '';
      var ph = fs.options[0] && !fs.options[0].value ? fs.options[0] : null;
      var label = fs.getAttribute('data-hk-option-name');
      fs.innerHTML = '';
      var p = document.createElement('option'); p.value = ''; p.disabled = true; p.textContent = body ? 'Choose ' + label : 'Choose Body Type first'; fs.appendChild(p);
      this.foldAll.forEach(function (a) {
        if (a.body !== body) return;
        var o = document.createElement('option'); o.value = a.value; o.setAttribute('data-hk-name', a.name);
        o.setAttribute('data-surcharge', a.sur); o.setAttribute('data-surcharge-label', a.pill); if (a.sold) o.setAttribute('data-sold-out', '');
        o.textContent = a.range + a.text.slice(a.name.length); // keep " (+$120.00)" / " - Sold out"
        fs.appendChild(o);
      });
      fs.value = keep; if (!keep) { p.selected = true; memo[pos] = ''; }
      fs.disabled = !body;
      var step = fs.closest('.hk-step'); if (step) step.classList.toggle('hk-step--waiting', !body);
    }

    onChange(sel) {
      if (sel === this.bodySel && this.foldSel) this.filterFold(true);
      state.get(this.formId)[sel.getAttribute('data-hk-option-position')] = sel.value;
      sel.classList.remove('hk-opt__control--missing');
      var step = sel.closest('.hk-step');
      if (step) step.classList.remove('hk-step--missing');
      this.sync(sel.getAttribute('data-hk-option-position'));
    }

    get complete() {
      return this.selects.every(function (s) { return !!s.value; });
    }

    missingNames() {
      return this.selects.filter(function (s) { return !s.value; }).map(function (s) { return s.getAttribute('data-hk-option-name'); });
    }

    selectedVariant() {
      // Shopify options only (numeric position), in position order: extra steps (Body Type, KDSS) are not variant options.
      var names = this.selects.filter(function (s) { return /^\d+$/.test(s.getAttribute('data-hk-option-position')); })
        .sort(function (a, b) { return a.getAttribute('data-hk-option-position') - b.getAttribute('data-hk-option-position'); })
        .map(function (s) { var o = chosenOption(s); return o ? o.getAttribute('data-hk-name') : null; });
      return this.variants.find(function (v) { return v.o.every(function (n, i) { return n === names[i]; }); }) || null;
    }

    sync(changedPosition) {
      var form = document.forms[this.formId];
      if (!form) return;
      var complete = this.complete;
      var memo = state.get(this.formId);
      var done = 0;

      // steps: check badge, surcharge pill, select empty state
      this.selects.forEach(function (s) {
        var o = chosenOption(s);
        if (o) done++;
        s.classList.toggle('is-empty', !o);
        var step = s.closest('.hk-step');
        if (!step) return;
        step.classList.toggle('is-chosen', !!o);
        var pill = step.querySelector('[data-hk-pill]');
        if (pill) {
          var label = o ? (o.getAttribute('data-surcharge-label') || '') : '';
          pill.textContent = label;
          pill.hidden = !label;
        }
      });

      var progress = this.scope.querySelector('[data-hk-progress]');
      if (progress) {
        var total = parseInt(progress.getAttribute('data-total'), 10) || this.selects.length;
        progress.textContent = done + ' of ' + total + (complete ? ' ✓' : '');
        progress.classList.toggle('is-complete', complete);
      }

      // hidden inputs feed Warehouse only once complete
      var changedHidden = null;
      this.scope.querySelectorAll('input[data-hk-hidden]').forEach(function (h) {
        var pos = h.getAttribute('data-hk-hidden');
        h.value = memo[pos] || '';
        if (complete) h.setAttribute('data-option-position', pos);
        else h.removeAttribute('data-option-position');
        if (pos === changedPosition) changedHidden = h;
      });

      form.classList.toggle('hk-incomplete', !complete);

      // Not the disabled attribute: a disabled submit swallows clicks and Enter, so an early add could never be caught.
      form.querySelectorAll('.product-form__add-button[type="submit"]').forEach(function (btn) {
        if (btn.classList.contains('button--disabled')) return; // genuinely sold out / unavailable
        if (complete) {
          btn.removeAttribute('aria-disabled');
          btn.removeAttribute('data-hk-locked');
        } else {
          btn.setAttribute('aria-disabled', 'true');
          btn.setAttribute('data-hk-locked', '');
        }
      });

      // hint row
      var msg = this.scope.querySelector('[data-hk-msg]') || form.querySelector('[data-hk-msg]');
      if (msg) {
        var text = msg.querySelector('[data-hk-msg-text]') || msg;
        if (complete) {
          msg.hidden = true;
          msg.classList.remove('hk-kit__hint--alert');
        } else {
          text.textContent = 'Choose ' + listSentence(this.missingNames()) + ' to add to cart';
          msg.hidden = false;
        }
      }

      // "Your kit" summary
      var summary = this.scope.querySelector('[data-hk-summary]');
      if (summary) {
        if (complete) {
          var values = summary.querySelector('[data-hk-summary-values]');
          if (values) {
            values.textContent = '';
            this.selects.forEach(function (s, i) {
              if (i) values.appendChild(document.createTextNode(' · '));
              var span = document.createElement('span');
              span.className = 'hk-nw';
              var nm = chosenOption(s).getAttribute('data-hk-name');
              if (s.hasAttribute('data-hk-fold') && nm.indexOf(' · ') > -1) nm = nm.slice(nm.indexOf(' · ') + 3);
              span.textContent = nm;
              values.appendChild(span);
            });
          }
          var v = this.selectedVariant();
          var price = summary.querySelector('[data-hk-summary-price]');
          if (price) price.textContent = v ? v.p : '';
          summary.hidden = false;
        } else {
          summary.hidden = true;
        }
      }

      // Hand the full combination to Warehouse only once everything is chosen.
      if (complete && changedPosition && changedHidden) {
        changedHidden.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    flagMissing() {
      var first = null;
      this.selects.forEach(function (s) {
        if (s.value) return;
        s.classList.add('hk-opt__control--missing');
        var step = s.closest('.hk-step');
        if (step) {
          step.classList.remove('hk-step--missing');
          void step.offsetWidth; // restart the pulse animation
          step.classList.add('hk-step--missing');
        }
        if (!first) first = s;
      });
      if (first) first.focus({ preventScroll: true });
      if (first && first.getBoundingClientRect().top < 80) first.scrollIntoView({ block: 'center', behavior: 'smooth' });
      var msg = this.scope.querySelector('[data-hk-msg]');
      if (msg) msg.classList.add('hk-kit__hint--alert');
    }
  }

  window.customElements.define('hk-option-picker', HkOptionPicker);

  // form.id would return the hidden <input name="id">, so always read the attribute.
  function guard(form, e) {
    if (!form || !form.classList || !form.classList.contains('hk-incomplete')) return false;
    e.preventDefault();
    e.stopImmediatePropagation();
    var picker = document.querySelector('hk-option-picker[form-id="' + form.getAttribute('id') + '"]');
    if (picker) picker.flagMissing();
    return true;
  }

  // Capture phase: runs before Warehouse's <buy-button> click handler (which adds to cart directly)
  // and before dynamic checkout buttons. Enter in the quantity field also arrives here as a click.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('.product-form__add-button, .shopify-payment-button');
    if (!btn) return;
    guard(btn.form || btn.closest('form'), e);
  }, true);

  document.addEventListener('submit', function (e) { guard(e.target, e); }, true);
})();
